// src/features/devices/thunks/moveDeviceOnDragEnd.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppDispatch, IAppThunk, IRootState } from "../../app/store";
import { IRoomWithCeilingGrid } from "../../Room/roomTypes";
import { selectActiveMcbimSpace, selectHttpMcbimSpacesResult } from "../../McBim/mcbimSelectors";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  setDeviceOnTheMoveInRedux,
  setSelectedSpaceNumberInRedux,
  thunkToast,
} from "../designSlice";
import { selectDeviceById, selectHttpDevices } from "./deviceSelectors";
import {
  getCenterOfBoundingBox,
  getRandomCoordinateInPolygon,
  PartialMcBimSpace,
  roomHasCeilingCoordinates,
} from "../../utils";
import { IBoundingBox, IDevice, IDeviceType, IRoomSummary } from "@backend/src/schemas";
import {
  adjustObject,
  calculateRoomGrid,
  copyDevicesFromASpaceToAnotherSpace,
  getRoomPic,
} from "../../snapToGrid/snapToGrid";
import { openapi } from "../../../openapi";
import {
  selectActiveRoom,
  selectHttpGetRoomsCurrentFloorResult,
  selectHttpSingleRoomFull,
} from "../../Room/roomSelectors";
import { selectCompCodePath, selectProjectNo } from "../../projectSelectors";
import {
  calculateTileBoundingBoxFromCoordinateAndDirection,
  convertAVectorTo60cm,
  convertMcBimItemToDevice,
  getPointASubPointB,
  IPointAPointB,
  moveBoundingBox,
} from "./deviceUtils";
import { convertMlResponseToDevice } from "../designThunk";
import { selectDevicesExistInRoom } from "./deviceSlice";
import { DEVICE_DISCIPLINE_MAPPING } from "../../enums";

// 🔎 Selektorer — bytt ut til dine faktiske selektorer
const selectSnapToGrid = (s: IRootState) => s.design.snapToGrid;

type Payload = { id: string; points: number[][] };

export const thunkMoveDeviceOnDragEnd = createAsyncThunk<
  void,
  Payload,
  { state: IRootState; dispatch: AppDispatch }
>("devices/moveOnDragEnd", async ({ id, points }, { getState, dispatch, rejectWithValue }) => {
  const state = getState();

  // Rensa drag-state direkt
  dispatch(setDeviceOnTheMoveInRedux(undefined));

  const devices = selectHttpDevices(state);
  const rooms = selectHttpGetRoomsCurrentFloorResult(state);
  const mcbimSpaces = selectHttpMcbimSpacesResult(state);
  const snapToGrid = selectSnapToGrid(state);

  const device = devices.find((d) => d._id === id);
  if (!device) {
    // Inga alerts i thunk – kasta kontrollerat fel
    return rejectWithValue("Device not found");
  }

  const centerOfBoundingBox = getCenterOfBoundingBox(points);
  const activeRoom = selectHttpSingleRoomFull(state);
  const mcbimSpace = mcbimSpaces.find((s: any) => s.name === device.roomNumber);

  // Bygg upp nytt device-objekt
  const updated = {
    room: activeRoom,
    mcbimSpace,
    device: {
      ...device,
      coordinate: centerOfBoundingBox,
      boundingBox: points as IBoundingBox,
      updatedAt: new Date().toISOString(),
    },
  };

  // Snap til takrutenett om tilgjengelig
  if (activeRoom && roomHasCeilingCoordinates(activeRoom) && mcbimSpace?.polygon && snapToGrid) {
    try {
      const grid = calculateRoomGrid(
        getRoomPic(mcbimSpace.polygon, undefined, activeRoom.ceilingCoordinate)
      );

      const adjusted = adjustObject(
        { boundingBox: updated.device.boundingBox, direction: updated.device.direction },
        grid
      );

      if (adjusted) {
        const bb = adjusted.boundingBox;
        updated.device.coordinate = getCenterOfBoundingBox(bb);
        updated.device.boundingBox = bb;
      }
    } catch (e) {
      // håll tyst i UI men logga i konsolen
      console.warn("Error adjusting device position", e);
    }
  }

  // RTK Query-anrop via dispatch
  await dispatch(
    openapi.endpoints.putMultiplacerDeviceUpdate.initiate({
      body: { device: updated.device },
    })
  ).unwrap();
});

export const thunkStartMove =
  (id: string): IAppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const device = selectDeviceById(state, id);
    if (device?.roomNumber != null) {
      dispatch(setSelectedSpaceNumberInRedux(device.roomNumber));
    }
    dispatch(setDeviceOnTheMoveInRedux(id));
  };

export const copyDevicesFromStandardRoomToSelectedRoomThunk = (): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const compCodePath = selectCompCodePath(state);

    if (!compCodePath) {
      console.log("No compCodePath found in state");
      return;
    }
    const selectedSpaceNumber = state.design.selectedSpaceNumber;

    if (!selectedSpaceNumber) {
      console.log("No selected space number");
      return;
    }

    const selectedRoom = selectHttpSingleRoomFull(state);

    if (!selectedRoom) {
      console.log("Selected room not found in rooms");
      return;
    }

    if (!selectedRoom.rfpMalRom) {
      console.log("Selected room has no standard room defined");
      return;
    }

    const mcbimSpace = selectActiveMcbimSpace(state);
    if (!mcbimSpace) {
      console.log("No active McBim space found");
      return;
    }
    console.log(mcbimSpace);

    const devicesExistsInRoom = selectDevicesExistInRoom(state);
    if (devicesExistsInRoom) {
      dispatch(
        thunkToast({
          text: `Målrommet ${selectedRoom.rfpMalRom} inneholder allerede enheter. Kopiering av enheter fra standardrom er derfor ikke mulig.`,
          header: "Advarsel",
          isSuccess: false,
        })
      );
      console.log("Devices already exist in target room");
      return;
    }

    try {
      // 1) Hämta items i standardSpace via RTK Query
      const mcbimItemsInStandardSpace = await dispatch(
        openapi.endpoints.getMcbimItemsInSpace.initiate({
          projectNo: state.design.projectNo,
          spaceName: selectedRoom.rfpMalRom,
        })
      ).unwrap(); // <- här får du loss datan (t.ex. McBimItem[])

      const standardSpace = await dispatch(
        openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
          projectNo: state.design.projectNo,
          name: selectedRoom.rfpMalRom,
        })
      ).unwrap();

      if (!standardSpace) {
        dispatch(
          thunkToast({
            text: `Standardrommet ${selectedRoom.rfpMalRom} ble ikke funnet.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        console.log("Standard space not found");
        return;
      }

      if (mcbimItemsInStandardSpace.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter ble funnet i standardrommet ${selectedRoom.rfpMalRom}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        console.log("No items found in standard room");
        return;
      }

      const devicesToCopyFromStandardSpace = mcbimItemsInStandardSpace.reduce((acc, mcbimItem) => {
        const device = convertMcBimItemToDevice({
          mcBimItem: mcbimItem,
          roomNumber: selectedSpaceNumber,
          compCodePath: compCodePath,
          projectNo: getState().design.projectNo,
        });
        if (device != null) {
          device.roomNumber = selectedSpaceNumber;
          device.floor = mcbimSpace.buildingStory?.name ?? "unknown";
          acc.push(device);
        }
        return acc;
      }, [] as IDevice[]);

      console.log("Standard room items", devicesToCopyFromStandardSpace);

      const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
        devicesToCopy: devicesToCopyFromStandardSpace,
        spaceToCopyFrom: standardSpace as PartialMcBimSpace,
        spaceToCopyTo: mcbimSpace as PartialMcBimSpace,
        roomToCopyTo: selectedRoom as IRoomSummary,
      });
      if (copiedDevices.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter kunne kopieres fordi målrommet skiller seg for mye fra kildrommet ${standardSpace?.name}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        return;
      }

      dispatch(addNonPublischedDevicesFromAlgosInRedux(copiedDevices));
    } catch (error) {
      console.error("Failed to load standard room items", error);
    }
  };
};

export const thunkAddMlDevicesToSelectedRoom = (): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const projectNo = selectProjectNo(state);
    const floorAndJob = getState().design.floorAndJob;

    if (floorAndJob?.floorName == null) {
      console.log("floorName is null");
      return;
    }

    const selectedRoom = selectHttpSingleRoomFull(state);

    if (!selectedRoom) {
      console.log("Selected room not found");
      return;
    }

    const mlPredictedDevices = selectedRoom.mlPredictedDevices;
    if (!mlPredictedDevices || mlPredictedDevices.length === 0) {
      dispatch(
        thunkToast({
          text: `Det er ingen AI-predikterte enheter for rommet ${selectedRoom.roomNumber}.`,
          header: "Info",
          isSuccess: true,
        })
      );
      return;
    }

    const converted = mlPredictedDevices.map((mlDevice) =>
      convertMlResponseToDevice(mlDevice, {
        projectNo: String(projectNo),
        floor: floorAndJob.floorName,
        roomNumber: selectedRoom.roomNumber,
        description: "",
        compCode: "",
      })
    );

    dispatch(addNonPublischedDevicesFromAlgosInRedux(converted));
  };
};

export const addDeviceTypeToSelectedRoomThunk = (deviceType: IDeviceType): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedSpaceNumber = state.design.selectedSpaceNumber;
    const projectNo = selectProjectNo(state);
    const compCodePath = selectCompCodePath(state);

    if (!selectedSpaceNumber) {
      console.log("No selected space number");
      return;
    }
    if (!compCodePath) {
      console.log("No compCodePath found in state");
      return;
    }
    const room = selectHttpSingleRoomFull(state);
    if (!room) {
      console.log("No active room found");
      return;
    }
    const mcbimSpace = selectActiveMcbimSpace(state);
    if (!mcbimSpace?.polygon) {
      console.log("No active McBim space found");
      return;
    }
    const firstLine = mcbimSpace.polygon[0];
    const secondLine = mcbimSpace.polygon[1];
    const pointAPointB: IPointAPointB = {
      pointA: {
        x: firstLine[0],
        y: firstLine[1],
      },
      pointB: {
        x: secondLine[0],
        y: secondLine[1],
      },
    };
    let _coord = getRandomCoordinateInPolygon(mcbimSpace.polygon as number[][]);
    const boundingBox = moveBoundingBox(
      calculateTileBoundingBoxFromCoordinateAndDirection(pointAPointB),
      _coord as { x: number; y: number }
    );
    const revitCeilingHeight = room.ceilingCoordinate ? room.ceilingCoordinate[2] : undefined;
    const device: IDevice = {
      description: deviceType.typeName ?? "",
      deviceCategory: deviceType.deviceCategory,
      discipline: DEVICE_DISCIPLINE_MAPPING[deviceType.deviceCategory],
      compCode: deviceType.compCode as string,
      boundingBox: boundingBox,
      coordinate: getCenterOfBoundingBox(boundingBox),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      direction: convertAVectorTo60cm(getPointASubPointB(pointAPointB)),
      revitCeilingHeight: revitCeilingHeight,
      floor: mcbimSpace.buildingStory?.name ?? "",
      projectNo: String(projectNo),
      roomNumber: room.roomNumber,
    };
    await dispatch(
      openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
        body: {
          devices: [device],
        },
      })
    );
  };
};
